
import { useMemo, useState, lazy, Suspense } from 'react';
import { useRouter } from '@/lib/router-compat';
import { observer } from 'mobx-react-lite';
import { useAuth } from '@/contexts/AuthContext';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useDeferredMount } from '@/hooks/useDeferredMount';
import { useContacts } from '@/hooks/useContacts';
import { useDeals } from '@/hooks/useDeals';
import { useProjects } from '@/hooks/useProjects';
import { useTasks } from '@/hooks/useTasks';
import { useGlobalTimeline } from '@/hooks/useTimeline';
import { Skeleton } from '@/components/ui/skeleton';
import { DashboardSkeleton } from '@/components/ui/skeletons';
import Link from '@/components/ui/link-compat';
import { preloadRoute } from '@/lib/nav-preload';

const ActivityTimeline = lazy(
  () => import('@/components/ui/activity-timeline').then(m => ({ default: m.ActivityTimeline }))
);

const ActivityTimelineFallback = () => (
  <div className="space-y-3">
    {Array.from({ length: 3 }).map((_, i) => (
      <div key={i} className="flex gap-3">
        <Skeleton className="h-8 w-8 rounded-full shrink-0" />
        <div className="space-y-2 flex-1">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      </div>
    ))}
  </div>
);
const OnboardingModal = lazy(
  () => import('@/components/onboarding/OnboardingModal').then(m => ({ default: m.OnboardingModal }))
);
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PageHeader } from '@/components/ui/page-header';
import { selectNeedsAttention } from '@/lib/dashboard/attention';
import { UpcomingTasksPanel } from '@/components/dashboard/upcoming-tasks-panel';
import { useTaskStore } from '@/stores/StoreProvider';
import { organizationApi } from '@/lib/api/auth';
import { DealStatus, CrmTaskStatus } from '@/types/crm';
import type { CrmTask } from '@/types/crm';
import {
  Users,
  FolderKanban,
  Handshake,
  CheckSquare,
  AlertCircle,
  Clock,
  ArrowRight,
  BellOff,
} from 'lucide-react';

function isOverdue(task: CrmTask): boolean {
  if (!task.dueDate || task.status === CrmTaskStatus.Completed) return false;
  return new Date(task.dueDate) < new Date(new Date().toDateString());
}

function isDueToday(task: CrmTask): boolean {
  if (!task.dueDate || task.status === CrmTaskStatus.Completed) return false;
  const today = new Date().toDateString();
  return new Date(task.dueDate).toDateString() === today;
}

function cardKeyHandler(onActivate: () => void) {
  return (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onActivate();
    }
  };
}

function StatTile({
  icon: Icon,
  label,
  value,
  subtitle,
  ariaLabel,
  onActivate,
  onPreload,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number;
  subtitle: React.ReactNode;
  ariaLabel: string;
  onActivate: () => void;
  onPreload: () => void;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      className="cursor-pointer border rounded-[10px] px-4 py-3.5 bg-card transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      onMouseEnter={onPreload}
      onTouchStart={onPreload}
      onClick={onActivate}
      onKeyDown={cardKeyHandler(onActivate)}
    >
      <div className="flex items-center gap-2">
        <span className="flex h-6 w-6 items-center justify-center rounded-md bg-muted text-muted-foreground">
          <Icon className="h-4 w-4" />
        </span>
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
      <div className="mt-2 text-2xl font-semibold">{value}</div>
      <div className="mt-0.5 text-xs text-muted-foreground">{subtitle}</div>
    </div>
  );
}

function DashboardPage() {
  usePageTitle('Dashboard');
  // Wait for the PageTransition fade-in (200ms) to largely complete before
  // mounting the heavy MobX-subscribed body. Otherwise the skeleton pops into
  // a content swap mid-fade and reads as jank.
  const bodyReady = useDeferredMount(180);

  if (!bodyReady) {
    return <DashboardSkeleton />;
  }

  return <DashboardBody />;
}

const DashboardBody = observer(function DashboardBody() {
  const router = useRouter();
  const { currentOrg, loadOrganizations, switchOrganization, organizations, pendingOnboarding, setPendingOnboarding } = useAuth();
  const timeline = useGlobalTimeline(20);
  const timelineEntries = timeline.entries;
  const { data: contacts } = useContacts();
  const { data: deals } = useDeals();
  const { data: projects } = useProjects();
  const { data: tasks } = useTasks();
  const taskStore = useTaskStore();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const showOnboarding = pendingOnboarding;
  const setShowOnboarding = setPendingOnboarding;
  const [orgName, setOrgName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const openDeals = deals?.filter((d) => d.status === DealStatus.Open).length || 0;

  const overdueTasks = useMemo(() => tasks?.filter(isOverdue).length || 0, [tasks]);
  const dueTodayTasks = useMemo(() => tasks?.filter(isDueToday).length || 0, [tasks]);
  const openTasks = useMemo(
    () => tasks?.filter((t) => t.status !== CrmTaskStatus.Completed).length || 0,
    [tasks],
  );

  // Session-scoped: tasks completed on this dashboard mount stay in the list
  // (struck through) until the dashboard remounts.
  const [justCompleted, setJustCompleted] = useState<Set<string>>(() => new Set());

  const upcomingTasks = useMemo(() => {
    if (!tasks) return [];
    return tasks
      .filter((t) => t.status !== CrmTaskStatus.Completed || justCompleted.has(t.id))
      .sort((a, b) => {
        // Sort by due date alone. isOverdue() excludes completed tasks, so
        // using it here would push a just-completed overdue row out of the
        // visible slice instead of leaving it in the slot it already had.
        const aTime = a.dueDate ? new Date(a.dueDate).getTime() : Number.POSITIVE_INFINITY;
        const bTime = b.dueDate ? new Date(b.dueDate).getTime() : Number.POSITIVE_INFINITY;
        return aTime - bTime;
      })
      .slice(0, 5);
  }, [tasks, justCompleted]);

  const totalProjectDeals = useMemo(
    () => projects?.reduce((sum, p) => sum + p.dealsCount, 0) || 0,
    [projects],
  );
  const totalProjectTasks = useMemo(
    () => projects?.reduce((sum, p) => sum + p.tasksCount, 0) || 0,
    [projects],
  );

  const wonThisWeek = useMemo(() => {
    if (!deals) return 0;
    const cutoff = Date.now() - 7 * 86_400_000;
    return deals.filter(
      (deal) => deal.status === DealStatus.Won && new Date(deal.updatedAt).getTime() >= cutoff,
    ).length;
  }, [deals]);

  const contactsAddedThisMonth = useMemo(() => {
    if (!contacts) return 0;
    const now = new Date();
    return contacts.filter((contact) => {
      const created = new Date(contact.createdAt);
      return (
        created.getFullYear() === now.getFullYear() &&
        created.getMonth() === now.getMonth()
      );
    }).length;
  }, [contacts]);

  const attentionItems = useMemo(
    () => selectNeedsAttention(tasks ?? [], deals ?? []),
    [tasks, deals],
  );
  const [dismissedAttention, setDismissedAttention] = useState<Set<string>>(new Set());
  const visibleAttention = attentionItems.filter(
    (item) => !dismissedAttention.has(item.kind === 'task' ? item.task!.id : item.deal!.id),
  );

  const handleCreateOrg= async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgName.trim()) return;

    try {
      setIsCreating(true);
      const org = await organizationApi.create({ name: orgName });
      await loadOrganizations();
      await switchOrganization(org.id);
      setOrgName('');
      setShowCreateForm(false);
      setShowOnboarding(true);
    } catch (error) {
      console.error('Failed to create organization:', error);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <PageHeader title="Dashboard" askAi className="mb-0" />

      {!currentOrg && organizations.length === 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Get Started</CardTitle>
            <CardDescription>Create your first organization to start using the CRM</CardDescription>
          </CardHeader>
          <CardContent>
            {!showCreateForm ? (
              <Button onClick={() => setShowCreateForm(true)}>Create Organization</Button>
            ) : (
              <form onSubmit={handleCreateOrg} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="orgName">Organization Name</Label>
                  <Input
                    id="orgName"
                    value={orgName}
                    onChange={(e) => setOrgName(e.target.value)}
                    placeholder="My Company"
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" disabled={isCreating}>
                    {isCreating ? 'Creating...' : 'Create'}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowCreateForm(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      )}

      {currentOrg && (
        <>
          {/* KPI tiles */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatTile
              icon={Users}
              label="Contacts"
              value={contacts?.length ?? 0}
              subtitle={
                <>
                  <span className="font-semibold text-green-600 dark:text-green-400">
                    ▲ {contactsAddedThisMonth}
                  </span>{' '}
                  this month
                </>
              }
              ariaLabel={`View contacts (${contacts?.length ?? 0})`}
              onActivate={() => router.push('/dashboard/contacts')}
              onPreload={() => preloadRoute('/dashboard/contacts', router)}
            />
            <StatTile
              icon={Handshake}
              label="Open deals"
              value={deals?.length ?? 0}
              subtitle={
                <>
                  <span className="font-semibold text-green-600 dark:text-green-400">
                    ▲ {wonThisWeek}
                  </span>{' '}
                  won this week · {openDeals} in pipeline
                </>
              }
              ariaLabel={`View deals (${deals?.length ?? 0})`}
              onActivate={() => router.push('/dashboard/deals')}
              onPreload={() => preloadRoute('/dashboard/deals', router)}
            />
            <StatTile
              icon={FolderKanban}
              label="Projects"
              value={projects?.length ?? 0}
              subtitle={`${totalProjectDeals} deals · ${totalProjectTasks} tasks`}
              ariaLabel={`View projects (${projects?.length ?? 0})`}
              onActivate={() => router.push('/dashboard/projects')}
              onPreload={() => preloadRoute('/dashboard/projects', router)}
            />
            <StatTile
              icon={CheckSquare}
              label="Open tasks"
              value={openTasks}
              subtitle={
                <>
                  <span className="text-destructive">{overdueTasks} overdue</span>
                  {' · '}
                  {dueTodayTasks} due today
                </>
              }
              ariaLabel={`View tasks (${openTasks} open)`}
              onActivate={() => router.push('/dashboard/tasks')}
              onPreload={() => preloadRoute('/dashboard/tasks', router)}
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
            <div className="space-y-4">
              <div className="border rounded-[10px] bg-card overflow-hidden">
                <div className="flex items-center justify-between px-4 pt-3.5 pb-2.5">
                  <h2 className="text-sm font-semibold">Upcoming tasks</h2>
                  <Button variant="ghost" size="sm" onClick={() => router.push('/dashboard/tasks')}>
                    View all
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
                <UpcomingTasksPanel
                  tasks={upcomingTasks}
                  onComplete={(task) => {
                    setJustCompleted((prev) => new Set(prev).add(task.id));
                    taskStore.update(task.id, { status: CrmTaskStatus.Completed });
                  }}
                  onOpen={(task) => router.push(`/dashboard/task?id=${task.id}`)}
                />
              </div>

              <div className="border rounded-[10px] bg-card overflow-hidden">
                <div className="flex items-center justify-between px-4 pt-3.5 pb-2.5">
                  <h2 className="text-sm font-semibold">Recent activity</h2>
                  <Link
                    href="/dashboard/activity"
                    prefetch={false}
                    className="hidden items-center gap-1 text-xs text-muted-foreground hover:text-foreground md:inline-flex"
                  >
                    View all
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
                <div className="px-4 pb-3">
                  <Suspense fallback={<ActivityTimelineFallback />}>
                    <ActivityTimeline
                      entries={timelineEntries}
                      variant="compact"
                      isLoading={timeline.isLoading}
                      showEntityLink
                    />
                  </Suspense>
                </div>
                {/* Touch targets: a full-width 44px row is reachable where the
                    small header link is not. */}
                <Link
                  href="/dashboard/activity"
                  prefetch={false}
                  className="flex h-11 items-center justify-center border-t text-[13px] font-medium text-foreground/80 hover:bg-muted/50 md:hidden"
                >
                  View all
                </Link>
              </div>
            </div>

            <div>
              <div className="border rounded-[10px] bg-card overflow-hidden">
                <div className="px-4 pt-3.5 pb-2.5">
                  <h2 className="text-sm font-semibold">Needs attention</h2>
                </div>
                {visibleAttention.length > 0 ? (
                  visibleAttention.map((item) => {
                    const isTask = item.kind === 'task';
                    const id = isTask ? item.task!.id : item.deal!.id;
                    const title = isTask
                      ? item.task!.title
                      : item.deal!.description || item.deal!.dealTypeName;
                    const subtitle =
                      item.reason === 'overdue'
                        ? `${item.ageDays} ${item.ageDays === 1 ? 'day' : 'days'} late · task${item.context ? ` · ${item.context}` : ''}`
                        : item.reason === 'today'
                          ? `Due today · task${item.context ? ` · ${item.context}` : ''}`
                          : `${item.ageDays} days no touch · deal${item.context ? ` · ${item.context}` : ''}`;

                    return (
                      <div
                        key={`${item.kind}-${id}`}
                        className="flex items-center gap-1 border-t pr-2 transition-colors hover:bg-muted/50"
                      >
                        <Link
                          href={
                            isTask
                              ? `/dashboard/task?id=${id}`
                              : `/dashboard/deal?id=${id}`
                          }
                          prefetch={false}
                          className="flex min-w-0 flex-1 items-center gap-2.5 px-4 py-2.5"
                        >
                          <span
                            className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${
                              item.reason === 'overdue'
                                ? 'bg-destructive/10 text-destructive'
                                : 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
                            }`}
                          >
                            {item.reason === 'overdue' ? (
                              <AlertCircle className="h-4 w-4" />
                            ) : item.reason === 'today' ? (
                              <Clock className="h-4 w-4" />
                            ) : (
                              <Handshake className="h-4 w-4" />
                            )}
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="line-clamp-2 text-[13px] font-medium">{title}</p>
                            <p className="line-clamp-2 text-[11.5px] text-muted-foreground">{subtitle}</p>
                          </div>
                        </Link>
                        <button
                          type="button"
                          aria-label={`Dismiss ${title}`}
                          className="shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                          onClick={() =>
                            setDismissedAttention((prev) => new Set(prev).add(id))
                          }
                        >
                          <BellOff className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    );
                  })
                ) : (
                  <div className="border-t px-4 py-3 text-sm text-muted-foreground">
                    Nothing needs attention
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      <OnboardingModal
        open={showOnboarding}
        onComplete={async () => {
          setShowOnboarding(false);
          await loadOrganizations();
        }}
        initialStep="select-industry"
      />
    </div>
  );
});

import { createFileRoute } from '@tanstack/react-router';
export const Route = createFileRoute('/dashboard/')({
  component: DashboardPage,
  pendingComponent: () => <DashboardSkeleton />,
  pendingMs: 0,
  pendingMinMs: 150,
});
